import { HTMLAttributes, useEffect, useRef } from 'react'
import { Transition } from 'react-transition-group'
import { Slot } from '@radix-ui/react-slot'

import Portal from '@/components/base/Portal'
import { useLenis } from '@/contexts/lenis'

import { cn } from '@/lib/utils'

export type ModalActions = {
  onClose?: () => void
  onClosed?: () => void
}

export type ModalProps = HTMLAttributes<HTMLDivElement> &
  ModalActions & {
    open?: boolean
    reveal?: boolean
    asChild?: boolean
  }

const Modal = ({ open, asChild, onClosed, ...props }: ModalProps) => {
  const Component = asChild ? Slot : 'div'
  const modal = useRef<HTMLDivElement>(null!)
  const { lenis } = useLenis()

  useEffect(() => {
    if (!lenis) return
    if (open) {
      lenis.stop()
    } else {
      lenis.start()
    }
  }, [open, lenis])

  return (
    <Portal selector="#modal">
      <Transition
        nodeRef={modal}
        in={open}
        timeout={{
          enter: 0,
          exit: 500
        }}
        onExited={onClosed}
        unmountOnExit
      >
        {(state) => (
          <Component
            ref={modal}
            className={cn({
              'opacity-0': state === 'entering',
              'opacity-100 transition-opacity duration-1000 ease-out-cubic':
                state === 'entered',
              'opacity-0 transition-opacity duration-300 ease-out-cubic':
                state === 'exiting'
            })}
            {...props}
          />
        )}
      </Transition>
    </Portal>
  )
}

export default Modal
